import { Fragment, useEffect, useState } from "react";
import "./DraggableList.css";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import DraggableListItem from "./DraggableListItem";
import FieldNames from "../../defaults/FieldNames.json";
import { FixedSizeList } from "react-window";
import { ListItem } from "@mui/material";
import { makeStyles } from '@mui/styles';
import ColorConstants from "../../defaults/ColorConstants";
import { useWindowDimensions } from "../popover/BulkApprovalDialog/BulkApprovalDialog";
import { getListCard } from "../../utils/ListCardHelper";

/**
 * DraggableList — Virtualized drag-and-drop list for grouping dialogs.
 *
 * @param {Object[]} items - The features to display in the list. Accepts either:
 *   - From AssociateDialog: `allSelectedFeatures` (candidates for a new group)
 *   - From GroupingsDialog: `selectedGroupItems` (items in an existing group)
 *   Both are arrays of feature objects with at minimum an `ID` and standard field attributes.
 * @param {boolean} isAssociate - True when used in the "create new group" flow (AssociateDialog).
 * @param {boolean} isInitialLoad - True on first load in GroupingsDialog (triggers sequence sort).
 * @param {function} onDragEnd - Callback when a drag-and-drop reorder completes.
 * @param {function} handleRemoval - Callback when the user removes an item from the list.
 */
export default function DraggableList({
  isAssociate,
  isInitialLoad,
  onDragEnd,
  handleRemoval,
  items = []
}) {
  const [filteredItemList, setFilteredItemList] = useState([]);
  const { height } = useWindowDimensions();
  const isDraggable = true;
  const listCount = 200;

  const useStyles = makeStyles({
    draggingListItem: {
      background: 'rgb(235,235,235)',
      width: '550px',
      height: '300px',
      maxHeight: '300px'
    },
    listItem: {
      width: '550px',
      height: '300px',
      maxHeight: '300px',
    },
    saveSpace: {
      paddingTop: '0px!important'
    },
    saveSpaceCarryover: {
      paddingTop: '0px!important',
      backgroundColor: ColorConstants.CarryOver
    },
    padding: {
      padding: "0px 8px"
    }
  });
  const classes = useStyles();


  useEffect(() => {
    let filteredItems = items;
    if (!isAssociate && isInitialLoad) {
      filteredItems = items.sort((a, b) =>
        a[FieldNames.treatmentActivityGroupItems.sequenceNumber] >
        b[FieldNames.treatmentActivityGroupItems.sequenceNumber]
          ? 1
          : -1
      );
    }

    if (filteredItems && filteredItems.length > 0) {
      setFilteredItemList(filteredItems);
    }
  }, [isAssociate, isInitialLoad, items]);

  const Row = (props) => {
    const { index, style, data } = props;
    const { itemList } = data;
    
    let feature = null;
    if (itemList && itemList.length > 0) {
      feature = itemList[index];
      if(!feature)
      {
        return <Fragment></Fragment>;
      }

      return (
        <DraggableListItem
          style={style}
          selectedFeature={feature}
          index={index}
          handleRemoval={handleRemoval}
          isDraggable={isDraggable}
          key={
            index
          }
      />
      );
    } else {
      return <Fragment></Fragment>;
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable 
      droppableId="droppable-list" 
      mode='virtual' // Not the same as IFPRS 'mode' that is set and read from the store
      renderClone={(provided, snapshot, rubric, style) => (
        <ListItem
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={snapshot.isDragging ? classes.draggingListItem : classes.listItem}
            component="div"
          >
          {getListCard(
            rubric.source.index, 
            filteredItemList[rubric.source.index], 
            isDraggable, 
            {
              styleHeight: style?.height ? style.height - 5 : 165,
              styleWidth: style?.width ? style.width : 520,
              cardSizes: FieldNames.listCardTypes.detailed, 
              arrowListCard : 'list-normal', 
              backgroundClassListCard: filteredItemList[rubric.source.index][FieldNames.featureFields.isCarryover] === FieldNames.yesNo.yes ? classes.saveSpaceCarryover : classes.saveSpace, 
              classesListCard: classes.saveSpace,
              backgroundClass: classes.saveSpace
            },
            {
              objectId: null,
              onClicked: handleRemoval,
              selectedFeatures: items,
              selectFeature: null,
              createActual: null, 
              reportActual: null, 
              isGroupingsLoading: false, 
              onGroupEdit: null, 
            }
            )
          }
          </ListItem>
      )}>
        {(provided) => (
          <FixedSizeList
              height={height}
              width={550}
              itemCount={
                filteredItemList && filteredItemList.length > 0 ? filteredItemList.length : listCount
              }
              itemSize={170}
              overscanCount={5}
              itemData={{itemList: filteredItemList }}
              className="list-container"
              outerRef={provided.innerRef}
               {...provided.droppableProps}
            >
              {Row}
            </FixedSizeList>
        )}
      </Droppable>
    </DragDropContext>
  );
}